export type AudioInputState = 'idle' | 'recording' | 'stopped';

export interface CapturedAudio {
  blob: Blob;
  mimeType: string;
}

const CANDIDATE_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
  // iOS/Safari supports none of the above; it does support recording as
  // audio/mp4 (AAC), which the server's allowlist also accepts.
  'audio/mp4',
];

function pickSupportedMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return undefined;
  }
  return CANDIDATE_MIME_TYPES.find((candidate) => MediaRecorder.isTypeSupported(candidate));
}

/**
 * Wraps microphone capture behind a small start/stop/dispose contract so the
 * SDK core never touches getUserMedia/MediaRecorder directly. For this MVP
 * it's a simple request/response flow: start() begins recording, stop()
 * resolves with the whole captured clip (no real-time streaming/WebRTC).
 *
 * The underlying MediaStream is acquired once and kept alive across
 * start()/stop() cycles (only the MediaRecorder is recreated per turn) so
 * the mic permission prompt — and, on hosts that treat every getUserMedia()
 * call as a fresh permission check (many embedded/infotainment WebViews do)
 * — is only ever shown once per session, not once per turn.
 */
export class AudioInput {
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: BlobPart[] = [];
  private state: AudioInputState = 'idle';
  private ensureStreamPromise: Promise<void> | null = null;
  /** In-flight start(), if any — stop() awaits it so a fast activate-then-immediately-deactivate never silently loses the recording. */
  private startPromise: Promise<void> | null = null;

  getState(): AudioInputState {
    return this.state;
  }

  /** The live mic stream while recording (or cached between turns), or null if none is held. */
  getStream(): MediaStream | null {
    return this.stream;
  }

  /**
   * Acquires and caches the mic stream/permission if not already held. Safe
   * to call any number of times, including concurrently — only the first
   * call (or one after the previous stream died) actually prompts/hits
   * getUserMedia; concurrent callers share that same in-flight request
   * rather than each triggering their own (which on some hosts could mean
   * multiple simultaneous permission prompts).
   */
  ensureStream(): Promise<void> {
    if (this.stream && this.streamIsLive()) return Promise.resolve();
    if (!this.ensureStreamPromise) {
      this.ensureStreamPromise = this.acquireStream().finally(() => {
        this.ensureStreamPromise = null;
      });
    }
    return this.ensureStreamPromise;
  }

  private async acquireStream(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('Microphone capture is not supported in this environment.');
    }
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  }

  private streamIsLive(): boolean {
    return this.stream?.getTracks().some((track) => track.readyState === 'live') ?? false;
  }

  start(): Promise<void> {
    if (this.state === 'recording') return this.startPromise ?? Promise.resolve();

    const promise = this.performStart();
    this.startPromise = promise;
    return promise.finally(() => {
      if (this.startPromise === promise) this.startPromise = null;
    });
  }

  private async performStart(): Promise<void> {
    await this.ensureStream();
    const mimeType = pickSupportedMimeType();
    const recorder = new MediaRecorder(
      this.stream as MediaStream,
      mimeType ? { mimeType } : undefined,
    );

    this.recorder = recorder;
    this.chunks = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) this.chunks.push(event.data);
    };
    recorder.start();
    this.state = 'recording';
  }

  /** Resolves with the captured clip, or null if nothing was recorded. */
  async stop(): Promise<CapturedAudio | null> {
    // A deactivate can arrive before the matching activate's start() has
    // actually finished acquiring the mic — wait for it so the recording
    // that's about to begin isn't dropped on the floor.
    if (this.startPromise) {
      await this.startPromise.catch(() => {});
    }

    if (this.state !== 'recording' || !this.recorder) {
      return null;
    }

    const recorder = this.recorder;
    return new Promise((resolve) => {
      recorder.onstop = () => {
        const mimeType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(this.chunks, { type: mimeType });
        this.chunks = [];
        // The underlying stream is deliberately kept alive here (only
        // released in dispose()) so the next start() reuses it instead of
        // requesting the mic — and its permission prompt — all over again.
        this.state = 'stopped';
        resolve(blob.size > 0 ? { blob, mimeType } : null);
      };
      recorder.stop();
    });
  }

  dispose(): void {
    if (this.recorder && this.state === 'recording') {
      try {
        this.recorder.stop();
      } catch {
        // Recorder may already be inactive; nothing to recover.
      }
    }
    this.releaseStream();
    this.recorder = null;
    this.chunks = [];
    this.state = 'idle';
  }

  private releaseStream(): void {
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }
}
